import { resolveClerkSignInProps } from "./authRedirect";

/**
 * Empty-draft front door: signed-out visitors see a sign-in/sign-up card
 * instead of the chat landing. After they authenticate, ChatView continues into
 * onboarding when that profile is still incomplete.
 */
export function shouldShowAuthWelcome(input: {
  readonly isDraftHero: boolean;
  readonly clerkEnabled: boolean;
  readonly isLoaded: boolean;
  readonly isSignedIn: boolean;
  /**
   * The landing carries a Clerk organization-invitation ticket (`__clerk_ticket`).
   * Force the auth card so the invitee can complete sign-up IN THIS APP — even
   * before Clerk hydrates and regardless of the draft-hero slot.
   */
  readonly hasInvitationTicket?: boolean;
}): boolean {
  if (input.hasInvitationTicket && input.clerkEnabled && !input.isSignedIn) return true;
  if (!input.isDraftHero || !input.clerkEnabled) return false;
  // Hold the empty-draft slot while Clerk hydrates so a returning session does
  // not flash the chat landing, then show the card only when signed out.
  if (!input.isLoaded) return true;
  return !input.isSignedIn;
}

export function resolveMorrowAuthWelcomeSignInProps(href: string, isElectron: boolean) {
  const redirect = resolveClerkSignInProps(href, isElectron);
  const forceRedirectUrl = redirect.forceRedirectUrl;
  const signUpForceRedirectUrl = redirect.signUpForceRedirectUrl ?? redirect.forceRedirectUrl;
  const urls = {
    // Classic sign-in keeps the footer "Don't have an account? Sign up" link.
    // withSignUp:true opts into Clerk's combined sign-in-or-up flow ("Continue
    // to …"), which hides that footer — desktop landing always hit that mode.
    withSignUp: false as const,
    ...(forceRedirectUrl ? { forceRedirectUrl } : {}),
    ...(signUpForceRedirectUrl ? { signUpForceRedirectUrl } : {}),
  };
  // Hash routing is for the hosted SPA (browser history). Electron already uses
  // Clerk's virtual router on the custom scheme, so don't fight the hash route.
  if (isElectron) return urls;
  return { ...urls, routing: "hash" as const };
}

/**
 * Embedded <SignIn> must use virtual routing. Hash/path routing only paints the
 * form when the URL is already on /sign-in, which leaves an empty card on the
 * landing hash (`#/`) and on Electron's custom scheme.
 */
export function resolveMorrowAuthWelcomeSignInComponentProps(href: string, isElectron: boolean) {
  const redirect = resolveMorrowAuthWelcomeSignInProps(href, isElectron);
  return { ...redirect, routing: "virtual" as const };
}
